export type FooterMenuTitleKey =
  | 'footer.company'
  | 'footer.services'
  | 'footer.aiSolutions'
  | 'footer.expertise'
  | 'footer.caseStudies'
  | 'footer.getInTouch'
  | 'footer.officeHQ'
  | 'footer.contact'
  | 'footer.office'
  | 'footer.legalLabel';

export type FooterLinkTextKey =
  | 'navigation.work'
  | 'navigation.services'
  | 'navigation.servicesDiscovery'
  | 'navigation.servicesDesign'
  | 'navigation.servicesDevelopment'
  | 'navigation.servicesDelivery'
  | 'navigation.about'
  | 'navigation.careers'
  | 'navigation.blog'
  | 'footer.contact'
  | 'footer.address.name'
  | 'footer.address.street'
  | 'footer.address.city'
  | 'footer.address.country'
  | 'footer.ai.strategyDiscovery'
  | 'footer.ai.searchKnowledge'
  | 'footer.ai.productDevelopment'
  | 'footer.ai.poweredAutomation'
  | 'footer.expertiseLinks.digitalTransformation'
  | 'footer.expertiseLinks.charterIndustry'
  | 'footer.legal.aiEthicalStatement'
  | 'footer.legal.cookiePolicy'
  | 'footer.legal.privacyPolicy'
  | 'footer.legal.legalInfo'
  | 'footer.legal.llmInfo';

export interface FooterLink {
  text: FooterLinkTextKey | string;
  href: string;
  isTranslationKey?: boolean;
  hasAiBadge?: boolean;
}

export interface FooterMenuItem {
  title: FooterMenuTitleKey;
  links: FooterLink[];
  addressUrl?: string;
}

export const legalLinks = [
  { href: '/legal-info', labelKey: 'footer.legal.legalInfo' },
  { href: '/privacy-policy', labelKey: 'footer.legal.privacyPolicy' },
  { href: '/cookie-policy', labelKey: 'footer.legal.cookiePolicy' },
  { href: '/ai-ethical-statement', labelKey: 'footer.legal.aiEthicalStatement' },
  { href: '/llm-info', labelKey: 'footer.legal.llmInfo' },
] as const;

export const contactInfo = {
  email: 'hello@workspace.hr',
  phone: '+385 95 396 3036',
} as const;

export const address = {
  streetAddress: 'Dračevac 3D',
  addressLocality: 'Split',
  postalCode: '21000',
  addressCountry: 'HR',
} as const;

export const companyMenu: FooterMenuItem = {
  title: 'footer.company',
  links: [
    { text: 'navigation.about', href: '/about', isTranslationKey: true },
    { text: 'navigation.blog', href: '/blog', isTranslationKey: true },
    { text: 'navigation.careers', href: '/careers', isTranslationKey: true },
    { text: 'footer.contact', href: '/contact', isTranslationKey: true },
    { text: 'navigation.work', href: '/work', isTranslationKey: true },
  ],
};

export const servicesMenu: FooterMenuItem = {
  title: 'footer.services',
  links: [
    { text: 'navigation.servicesDiscovery', href: '/services/discovery', isTranslationKey: true },
    { text: 'navigation.servicesDesign', href: '/services/design', isTranslationKey: true },
    { text: 'navigation.servicesDevelopment', href: '/services/development', isTranslationKey: true },
    { text: 'navigation.servicesDelivery', href: '/services/delivery', isTranslationKey: true },
  ],
};

export const aiSolutionsMenu: FooterMenuItem = {
  title: 'footer.aiSolutions',
  links: [
    {
      text: 'footer.ai.strategyDiscovery',
      href: '/services/ai-strategy-discovery',
      isTranslationKey: true,
      hasAiBadge: true,
    },
    {
      text: 'footer.ai.searchKnowledge',
      href: '/services/ai-search-knowledge',
      isTranslationKey: true,
      hasAiBadge: true,
    },
    {
      text: 'footer.ai.productDevelopment',
      href: '/services/ai-product-development',
      isTranslationKey: true,
      hasAiBadge: true,
    },
    {
      text: 'footer.ai.poweredAutomation',
      href: '/services/ai-powered-automation',
      isTranslationKey: true,
      hasAiBadge: true,
    },
  ],
};

export const expertiseMenu: FooterMenuItem = {
  title: 'footer.expertise',
  links: [
    {
      text: 'footer.expertiseLinks.digitalTransformation',
      href: '/digital-transformation',
      isTranslationKey: true,
    },
    {
      text: 'footer.expertiseLinks.charterIndustry',
      href: '/charter-industry',
      isTranslationKey: true,
    },
  ],
};

export const officeMenu: FooterMenuItem = {
  title: 'footer.officeHQ',
  links: [
    { text: 'footer.address.name', href: '#', isTranslationKey: true },
    { text: 'footer.address.street', href: '#', isTranslationKey: true },
    { text: 'footer.address.city', href: '#', isTranslationKey: true },
    { text: 'footer.address.country', href: '#', isTranslationKey: true },
  ],
};

export const getInTouchMenu: FooterMenuItem = {
  title: 'footer.getInTouch',
  links: [
    { text: contactInfo.email, href: `mailto:${contactInfo.email}` },
    { text: contactInfo.phone, href: `tel:${contactInfo.phone.replace(/\s/g, '')}` },
  ],
};

// Featured case study slugs (mirrors OurWorkSection's featured projects: first 4 in projects.config)
export const footerCaseStudySlugs = [
  'serwizz-cmms',
  'boat4you',
  'capax-service-report-app',
  'des-time-tracking',
] as const;

// Accent color mapping for showcase cards
export const caseStudyAccentColors: Record<
  (typeof footerCaseStudySlugs)[number],
  'orange' | 'blue' | 'cyan' | 'graphite'
> = {
  'serwizz-cmms': 'orange',
  boat4you: 'blue',
  'capax-service-report-app': 'graphite',
  'des-time-tracking': 'cyan',
};
